import React from 'react';
import { Tooltip, type TableColumnsType } from 'antd';
import { FilterFilled } from '@ant-design/icons';
import moment from 'moment-timezone';
import { Operations } from './Operations';
import * as Styled from './styled';
import * as Types from '../../types';
import { isInternal } from '../utils';
import { filterPermissionForDisabledFlag } from '../UserProfileEditor/contextCenterEligibility';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';

export const checkIfUserMatched = (profile: Types.UserProfileInfo, text?: string) => {
  if (!text) return true;

  const textStr = String(text).toLocaleLowerCase();
  return (
    String(profile.email).toLocaleLowerCase().includes(textStr) ||
    String(profile.firstName).toLocaleLowerCase().includes(textStr) ||
    String(profile.lastName).toLocaleLowerCase().includes(textStr)
  );
};

/**
 * Reconcile the table's selected rows across a server-side search/filter change.
 *
 * With antd's `preserveSelectedRowKeys`, a row can stay selected even after the current
 * (server-filtered) dataSource no longer contains it — e.g. select user A, search for B, select B.
 * In that case antd's `onChange` `selectedRecords` has an `undefined` hole for A (no record on this
 * page), which later crashes anything mapping `it.userId`. Resolve each selected key against the
 * records antd provided plus the previously-selected items, so a user selected before searching
 * keeps its full record; drop any key that still can't be resolved.
 */
export const reconcileSelectedItems = (
  selectedKeys: React.Key[],
  selectedRecords: (Types.UserProfileInfo | undefined)[],
  previousItems: Types.UserProfileInfo[],
): Types.UserProfileInfo[] => {
  const known = new Map<React.Key, Types.UserProfileInfo>();
  for (const item of previousItems) {
    known.set(item.userId, item);
  }
  for (const rec of selectedRecords) {
    if (rec) {
      known.set(rec.userId, rec);
    }
  }
  return selectedKeys
    .map((key) => known.get(key))
    .filter((item): item is Types.UserProfileInfo => item !== undefined);
};

// Nexa permission display mapping
const getNexaPermissionDisplay = (value: number | null | undefined) => {
  switch (value) {
    case null:
    case undefined:
      return 'No';
    case 1:
      return 'Use';
    case 2:
      return 'Enable';
    default:
      return 'No';
  }
};

// API Management permission display mapping
const getApiManagementPermissionDisplay = (value: number | null | undefined) => {
  switch (value) {
    case null:
    case undefined:
      return 'Hide';
    case 1:
      return 'View';
    case 2:
      return 'Edit';
    default:
      return 'Hide';
  }
};

/** Yes/No filter options for a boolean permission column (server-side; value is the string form). */
const BOOLEAN_FILTERS = [
  { text: 'Yes', value: 'true' },
  { text: 'No', value: 'false' },
];
const NEXA_FILTERS = [
  { text: 'No', value: '0' },
  { text: 'Use', value: '1' },
  { text: 'Enable', value: '2' },
];
const API_FILTERS = [
  { text: 'Hide', value: 'null' },
  { text: 'View', value: '1' },
  { text: 'Edit', value: '2' },
];

export const getColumns = ({
  user,
  permissionDefinitions = [],
  roleList = [],
  activePermKey,
  activePermValue,
  activeRoleId,
}: {
  user: Types.LoginUserProfile;
  permissionDefinitions?: Types.PermissionDescriptor[];
  /** roles to offer in the Role column filter */
  roleList?: { roleId: number; roleName: string }[];
  /** the one permission column currently filtered (server allows a single permission filter) */
  activePermKey?: string;
  activePermValue?: string;
  activeRoleId?: number;
}) => {
  const nexaFeatureEnabled = getFeature(featureNames.NEXA);

  // A permission column's filter is server-side (single permission at a time): reflect the active
  // filter as controlled state, and DISABLE the filter control on the OTHER permission columns while
  // one is active — the server rejects >1 permission filter (map ticket T8/T9).
  const permColumnFilterProps = (key: string, filters: { text: string; value: string }[]) => {
    const isActive = activePermKey === key;
    const anotherActive = activePermKey !== undefined && !isActive;
    return {
      filters,
      filterMultiple: false,
      // controlled by lifted state; no onFilter → filtering happens server-side, not on loaded rows
      filteredValue: isActive && activePermValue !== undefined ? [activePermValue] : null,
      // once one permission column is filtered, greys out the others' filter dropdowns — with a
      // tooltip explaining WHY (only one permission filter at a time), so the disabled icon isn't a
      // mystery to the user.
      filterIcon: (filtered: boolean) => {
        const icon = (
          <FilterFilled
            style={{ color: anotherActive ? '#d9d9d9' : filtered ? '#1890ff' : undefined }}
          />
        );
        return anotherActive ? (
          <Tooltip title="You can filter by one permission at a time. Clear the active permission filter to filter by this one.">
            {icon}
          </Tooltip>
        ) : (
          icon
        );
      },
      ...(anotherActive ? { filterDropdown: () => null } : {}),
    };
  };

  // Registry-defined boolean permissions (KV-stored) become generic checkmark columns — no
  // hardcoded column per permission (map tickets T1 + T2). Context Center is omitted unless
  // nexa-context-center is on for this operator.
  const kvBooleanColumns = filterPermissionForDisabledFlag(
    permissionDefinitions
      .filter((def) => def.kind === 'boolean' && def.storage?.kind === 'kv')
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    getFeature(featureNames.NEXA_CONTEXT_CENTER),
  ).map((def) => ({
    title: def.label,
    dataIndex: def.key,
    width: 110,
    ellipsis: true,
    render: (value) => {
      const text = value ? '✓' : '';
      return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
    },
    sorter: (a, b) => Number(Boolean(a[def.key])) - Number(Boolean(b[def.key])),
    ...permColumnFilterProps(def.key, BOOLEAN_FILTERS),
  }));

  return [
    {
      title: 'Username',
      dataIndex: 'email',
      width: 200,
      render: (value) => {
        return <Styled.Text>{value}</Styled.Text>;
      },
      sorter: (a, b) => String(a.email).localeCompare(String(b.email)),
    },
    {
      title: <span>Role</span>,
      dataIndex: 'roleName',
      width: 80,
      render: (value) => {
        return (
          <Styled.Text maxWidth="300px">
            <Tooltip placement="topLeft" title={value}>
              {value}
            </Tooltip>
          </Styled.Text>
        );
      },
      sorter: (a, b) => String(a.roleName).localeCompare(String(b.roleName)),
      // Role filter is server-side (independent of the single-permission rule): controlled by
      // lifted state, no onFilter.
      filters: roleList.map((r) => ({ text: r.roleName, value: String(r.roleId) })),
      filterMultiple: false,
      filteredValue: activeRoleId !== undefined ? [String(activeRoleId)] : null,
    },
    isInternal(user) && {
      title: 'Hidden',
      dataIndex: 'hidden',
      width: 75,
      render: (value) => {
        const text = value ? '✓' : '';
        return (
          <Tooltip placement="topLeft">
            <Styled.Text>{text}</Styled.Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => Number(a.hidden ?? 0) - Number(b.hidden ?? 0),
    },
    {
      title: 'Audience',
      dataIndex: 'ciEnabled',
      width: 100,
      ellipsis: true,
      render: (value) => {
        const text = value ? '✓' : '';
        return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
      },
      sorter: (a, b) => Number(a.ciEnabled ?? 0) - Number(b.ciEnabled ?? 0),
      ...permColumnFilterProps('ciEnabled', BOOLEAN_FILTERS),
    },
    {
      title: 'Metadata Service',
      dataIndex: 'mdsEnabled',
      width: 110,
      ellipsis: true,
      render: (value) => {
        const text = value ? '✓' : '';
        return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
      },
      sorter: (a, b) => Number(a.mdsEnabled ?? 0) - Number(b.mdsEnabled ?? 0),
      ...permColumnFilterProps('mdsEnabled', BOOLEAN_FILTERS),
    },
    {
      title: 'Precision Policy admin',
      dataIndex: 'canEditPrecisionPolicy',
      width: 110,
      ellipsis: true,
      render: (value) => {
        const text = value ? '✓' : '';
        return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
      },
      sorter: (a, b) =>
        Number(a.canEditPrecisionPolicy ?? 0) - Number(b.canEditPrecisionPolicy ?? 0),
      ...permColumnFilterProps('canEditPrecisionPolicy', BOOLEAN_FILTERS),
    },
    {
      title: 'App Activation admin',
      dataIndex: 'canCreateActivation',
      width: 110,
      ellipsis: true,
      render: (value) => {
        const text = value ? '✓' : '';
        return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
      },
      sorter: (a, b) => Number(a.canCreateActivation ?? 0) - Number(b.canCreateActivation ?? 0),
      ...permColumnFilterProps('canCreateActivation', BOOLEAN_FILTERS),
    },
    nexaFeatureEnabled && {
      title: 'Nexa',
      dataIndex: 'nexaPermission',
      width: 80,
      ellipsis: true,
      render: (value) => {
        const text = getNexaPermissionDisplay(value);
        return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
      },
      sorter: (a, b) => Number(a.nexaPermission ?? 0) - Number(b.nexaPermission ?? 0),
      ...permColumnFilterProps('nexaPermission', NEXA_FILTERS),
    },
    {
      title: 'API Management',
      dataIndex: 'apiManagementPermission',
      width: 120,
      ellipsis: true,
      render: (value) => {
        const text = getApiManagementPermissionDisplay(value);
        return <Styled.Text maxWidth="300px">{text}</Styled.Text>;
      },
      sorter: (a, b) =>
        Number(a.apiManagementPermission ?? 0) - Number(b.apiManagementPermission ?? 0),
      ...permColumnFilterProps('apiManagementPermission', API_FILTERS),
    },
    ...kvBooleanColumns,
    {
      title: <span>Last access</span>,
      dataIndex: 'lastAccessedAt',
      width: 110,
      ellipsis: true,
      render: (value) => {
        const format = 'MMM Do YYYY';
        const text = value ? moment(value).format(format) : 'Never';
        return <Styled.Text>{text}</Styled.Text>;
      },
      sorter: (a, b) => {
        return (a.lastAccessedAt ?? 0) - (b.lastAccessedAt ?? 0);
      },
      defaultSortOrder: 'descend',
    },
    {
      title: <span>Updated On</span>,
      dataIndex: 'updatedAt',
      width: 110,
      ellipsis: true,
      render: (value) => {
        const format = 'MMM Do YYYY';
        const text = value ? moment(value).format(format) : '';
        return <Styled.Text>{text}</Styled.Text>;
      },
      sorter: (a, b) => {
        return (
          (a.updatedAt ? moment(a.updatedAt).valueOf() : 0) -
          (b.updatedAt ? moment(b.updatedAt) : 0)
        );
      },
    },
    {
      title: <span>Updated By</span>,
      dataIndex: 'updatedBy',
      width: 150,
    },
    {
      title: <span>Created On</span>,
      dataIndex: 'createdAt',
      width: 110,
      ellipsis: true,
      render: (value) => {
        const format = 'MMM Do YYYY';
        const text = value ? moment(value).format(format) : '';
        return <Styled.Text>{text}</Styled.Text>;
      },
      sorter: (a, b) => {
        return (
          (a.createdAt ? moment(a.createdAt).valueOf() : 0) -
          (b.createdAt ? moment(b.createdAt) : 0)
        );
      },
    },
    {
      title: <span>Created By</span>,
      dataIndex: 'createdBy',
      width: 150,
    },
    {
      title: 'Action',
      dataIndex: '$action',
      ellipsis: true,
      width: 60,
      render: (_, userProfile) => <Operations userProfile={userProfile} />,
    },
  ].filter(Boolean) as TableColumnsType<Types.UserProfileInfo>;
};
